import type { CardPreviewDensity } from "@/components/shapes/card-previews";
import { GadgetCard } from "@/components/shapes/shared/card-visual-parts";
import { OPENED_PART } from "@/components/shapes/shared/object-frame";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { framesFlightMessages } from "@/i18n/messages/frames-flight";
import type { FlightEndpoint, FlightFields } from "./flight-content";

function Endpoint({
  endpoint,
  end,
  testId,
}: {
  endpoint: FlightEndpoint;
  end?: boolean;
  testId: string;
}) {
  return (
    <span
      className={end ? "min-w-0 text-right" : "min-w-0"}
      data-testid={testId}
    >
      <b className="block font-display text-[2em] font-medium leading-none tracking-[-0.01em]">
        {endpoint.code || "—"}
      </b>
      <small className="mt-[0.3em] block truncate text-[0.78em] text-[color:var(--gadget-ink-soft)]">
        {[endpoint.city, endpoint.time].filter(Boolean).join(" · ")}
      </small>
    </span>
  );
}

/** The boarding pass below the opened card: its details and its barcode. */
function FlightPass({
  fields,
  testIdPrefix,
}: {
  fields: FlightFields;
  testIdPrefix: string;
}) {
  const t = useT(framesFlightMessages);
  const { details, barcode, barcodeUrl } = fields;
  return (
    <>
      {details.length > 0 && (
        <div
          className="flex flex-wrap justify-between gap-x-[1em] gap-y-[0.4em]"
          data-testid={`${testIdPrefix}-details`}
        >
          {details.map((detail) => (
            <span
              key={detail.key}
              className="min-w-0"
              data-testid={`${testIdPrefix}-meta-${detail.key}`}
            >
              <span className="block text-[0.78em] text-[color:var(--gadget-ink-soft)]">
                {t(detail.key)}
              </span>
              <span className="block truncate text-[0.9em] font-medium">
                {detail.value}
              </span>
            </span>
          ))}
        </div>
      )}
      {(barcode || barcodeUrl) && (
        <div className="min-w-0" data-testid={`${testIdPrefix}-barcode`}>
          {barcodeUrl && (
            <img
              src={barcodeUrl}
              alt={t("barcodeAlt")}
              draggable={false}
              className="max-h-[4em] max-w-full rounded-[0.4em] bg-[var(--gadget-ink)] object-contain p-[0.3em]"
              data-testid={`${testIdPrefix}-barcode-image`}
            />
          )}
          {barcode && (
            <p className="mt-[0.3em] break-all font-mono text-[0.78em] text-[color:var(--gadget-ink-soft)]">
              {barcode}
            </p>
          )}
        </div>
      )}
    </>
  );
}

/**
 * A flight (mockup `.fl`), the card and its opened window: airline and
 * status, the route and its path. Opened, the boarding pass follows.
 */
export function FlightFace({
  fields,
  density,
  paint,
  opened = false,
  testIdPrefix,
}: {
  fields: FlightFields;
  density: CardPreviewDensity;
  /** The card's paint; an opened face wears its frame's. */
  paint?: string;
  opened?: boolean;
  /** The card's test id (`flight-card`), the prefix of its parts' ids. */
  testIdPrefix: string;
}) {
  const heading = [fields.heading, fields.statusTag]
    .filter(Boolean)
    .join(" · ");
  return (
    <GadgetCard
      paint={paint}
      density={density}
      className={opened ? OPENED_PART : undefined}
      data-testid={opened ? `${testIdPrefix}-face` : testIdPrefix}
    >
      <p
        className="truncate text-[0.86em] text-[color:var(--gadget-ink-soft)]"
        data-testid={`${testIdPrefix}-heading`}
      >
        {heading}
      </p>
      <div className="flex items-end justify-between gap-3">
        <Endpoint endpoint={fields.from} testId={`${testIdPrefix}-from`} />
        <Endpoint endpoint={fields.to} end testId={`${testIdPrefix}-to`} />
      </div>
      <div
        aria-hidden="true"
        className="relative mx-0.5 h-0.5 shrink-0 bg-[var(--gadget-track)]"
      >
        <Icons.Plane className="absolute left-1/2 top-1/2 h-[1.1em] w-[1.1em] -translate-x-1/2 -translate-y-1/2 fill-current" />
      </div>
      {opened && <FlightPass fields={fields} testIdPrefix={testIdPrefix} />}
    </GadgetCard>
  );
}
